import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  useToast,
} from "@chakra-ui/react";
import { Form, Formik, FormikHelpers } from "formik";
import React, { useCallback } from "react";
import { Helmet } from "react-helmet";

import MainBreakerInput from "../../../../../components/elements/mainBreakerInput";
import NumberInput from "../../../../../components/elements/numberInput";
import { GENERIC_SAVED_MESSAGE } from "../../../../../constants/lang/en";
import {
  DEFAULT_BREAKER_ROWS,
  DEFAULT_MAIN_BREAKER_OR_LUG,
  DEFAULT_MAIN_BREAKER_OR_LUG_POLES,
  DEFAULT_MAIN_BREAKER_OR_LUG_POSITION,
  getDefaultBreakerColumns,
} from "../../../../../constants/misc";
import {
  AssetDocument,
  AssetWithRelationsFragmentFragment,
  useAssetUpdateConfigMutation,
} from "../../../../../graphql/graphql";
import setServerErrors, {
  setGenericMessage,
} from "../../../../../utils/serverErrors";
import {
  mainBreakerOrLugSchema,
  numberOfColumnsSchema,
  numberOfRowsSchema,
  yupObject,
} from "../../../../../utils/validation";

interface ElectricalPanelGridSettingEditProps {
  asset: AssetWithRelationsFragmentFragment;
  isOpen: boolean;
  onClose: () => void;
}

const ElectricalPanelGridSettingEdit: React.FC<ElectricalPanelGridSettingEditProps> =
  ({ asset, isOpen, onClose }) => {
    const toast = useToast();
    const [assetUpdateConfigMutation, { loading }] =
      useAssetUpdateConfigMutation({
        refetchQueries: [{ query: AssetDocument, variables: { id: asset.id } }],
        awaitRefetchQueries: true,
      });

    const handleSubmit = useCallback(
      async (
        data: AssetConfigData,
        { setFieldError }: FormikHelpers<AssetConfigData>
      ) => {
        try {
          const { data: serverData, errors } = await assetUpdateConfigMutation({
            variables: {
              id: asset.id,
              config: { ...asset.config, ...data },
            },
          });
          if (errors) {
            setServerErrors(errors, setFieldError);
          } else if (serverData) {
            onClose();
            toast({
              description: GENERIC_SAVED_MESSAGE,
              status: "success",
              position: "top",
              isClosable: true,
            });
            return;
          }
        } catch (error) {
          toast({
            description: setGenericMessage(error),
            status: "error",
            position: "top",
            isClosable: true,
          });
        }
      },
      [asset, assetUpdateConfigMutation, onClose, toast]
    );

    return (
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <Helmet>
          <title>Edit Electrical Panel Settings</title>
        </Helmet>
        <DrawerOverlay>
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>Edit Electrical Panel Settings</DrawerHeader>
            <DrawerBody>
              <Formik
                initialValues={{
                  numberOfRows:
                    asset.config.numberOfRows || DEFAULT_BREAKER_ROWS,
                  numberOfColumns:
                    asset.config.numberOfColumns ||
                    getDefaultBreakerColumns(asset.assetType.name),
                  mainBreakerOrLug:
                    asset.config.mainBreakerOrLug ||
                    DEFAULT_MAIN_BREAKER_OR_LUG,
                  mainBreakerOrLugPoles:
                    asset.config.mainBreakerOrLugPoles ||
                    DEFAULT_MAIN_BREAKER_OR_LUG_POLES,
                  mainBreakerOrLugPosition:
                    asset.config.mainBreakerOrLugPosition ||
                    DEFAULT_MAIN_BREAKER_OR_LUG_POSITION,
                }}
                validationSchema={assetConfigValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                <Form id="asset_file_edit" noValidate>
                  <NumberInput
                    label="Number of rows"
                    name="numberOfRows"
                    defaultValue={
                      asset.config.numberOfRows || DEFAULT_BREAKER_ROWS
                    }
                    min={1}
                    max={200}
                    autoFocus
                  />
                  <NumberInput
                    label="Number of columns"
                    name="numberOfColumns"
                    defaultValue={
                      asset.config.numberOfColumns ||
                      getDefaultBreakerColumns(asset.assetType.name)
                    }
                    min={1}
                    max={200}
                  />
                  <MainBreakerInput />
                  <Box marginY="10">
                    <Button
                      width="full"
                      type="submit"
                      isLoading={loading}
                      form="asset_file_edit"
                    >
                      Save
                    </Button>
                  </Box>
                </Form>
              </Formik>
            </DrawerBody>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    );
  };

export default ElectricalPanelGridSettingEdit;

const assetConfigValidationSchema = yupObject().shape({
  numberOfColumns: numberOfColumnsSchema,
  numberOfRows: numberOfRowsSchema,
  mainBreakerOrLug: mainBreakerOrLugSchema,
});

export type AssetConfigData = {
  numberOfColumns: number;
  numberOfRows: number;
  mainBreakerOrLug: boolean;
};
